// Talks to the Dresden Print agent, a small program that runs on the counter
// machine and forwards a docket to the thermal printer. The browser cannot
// reach a USB printer itself, and its own print dialog does not handle an 80mm
// docket well, which is the whole reason the agent exists.

const PORT = 9110;

// Safari refuses to let an HTTPS page call http://127.0.0.1. Supporting it needs
// a hostname that resolves to loopback plus a real certificate, the same trick
// QZ Tray uses. Until that DNS record and certificate exist, leave this null -
// probing a domain that does not resolve just produces confusing console errors.
const TLS_HOST = null; // e.g. 'print.dresden.vision'

// Loopback first: it is what Chrome and Firefox use, and it answers instantly.
const ENDPOINTS = [`http://127.0.0.1:${PORT}`].concat(
  TLS_HOST ? [`https://${TLS_HOST}:${PORT}`] : [],
);

const STATUS_TIMEOUT = 1500;
// Chrome holds a call to this computer until staff answer its "Local network
// access" prompt. Cutting that off at 1.5s took the prompt away before anyone
// could click Allow, so the agent was never reached (DP-5212, Geelong Mac).
const PERMISSION_TIMEOUT = 60000;
// Longer than the agent's own timeout, so the agent's error message reaches
// the screen rather than the browser giving up first.
const PRINT_TIMEOUT = 50000;

let resolved = null;

// Set once this browser has reached the agent, so a later failure can say
// "installed but not running" instead of showing the install steps again.
const SEEN_KEY = 'dresdenPrintSeen';

export const PrintResult = {
  PRINTED: 'printed',
  NO_AGENT: 'no-agent',
  FAILED: 'failed',
};

function withTimeout(url, options, ms) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);

  return fetch(url, { ...options, signal: controller.signal }).finally(() => clearTimeout(timer));
}

// Chrome's permission for this site to reach apps on this computer:
// 'granted', 'prompt' (Chrome will ask), 'denied', or null when the browser
// has no such permission.
export async function localAccess() {
  if (!navigator.permissions) return null;
  for (const name of ['local-network-access', 'loopback-network']) {
    try {
      return (await navigator.permissions.query({ name })).state;
    } catch (err) {
      // This browser does not know that permission.
    }
  }

  return null;
}

// Finds whichever endpoint this browser can reach, and remembers it.
async function endpoint() {
  if (resolved) return resolved;

  for (const base of ENDPOINTS) {
    if (await reachable(base)) {
      resolved = base;
      return base;
    }
  }

  return null;
}

async function answers(base) {
  try {
    return (await withTimeout(`${base}/status`, {}, STATUS_TIMEOUT)).ok;
  } catch (err) {
    // Agent not reachable here. On Safari the loopback call is blocked
    // outright, which lands in this branch too.
    return false;
  }
}

// While Chrome's permission prompt is open the first call waits on it, and
// Chrome holds any other call to this computer behind it. Watch the permission:
// once staff answer, drop the waiting call and, if allowed, call again.
async function reachable(base) {
  if ((await localAccess()) !== 'prompt') return answers(base);

  const controller = new AbortController();
  const waiting = fetch(`${base}/status`, { signal: controller.signal }).then(
    res => res.ok,
    () => false,
  );

  const answered = new Promise(resolve => {
    const started = Date.now();
    const timer = setInterval(async () => {
      const state = await localAccess();
      if (state === 'prompt' && Date.now() - started < PERMISSION_TIMEOUT) return;
      clearInterval(timer);
      controller.abort();
      resolve(state === 'granted' ? answers(base) : false);
    }, 500);
  });

  // Whichever comes first: the waiting call answering, or the permission
  // being decided.
  return Promise.race([waiting.then(ok => ok || answered), answered]);
}

// Whether the agent is running, and which printer it will use. Null means no
// agent, which is the cue to show install instructions.
export async function status() {
  const base = await endpoint();
  if (!base) return null;

  try {
    // Tell the agent which API this site uses, so it can fetch its own updates.
    const api = encodeURIComponent(new URL(process.env.VUE_APP_NEW_API_URL).origin);
    const res = await withTimeout(`${base}/status?api=${api}`, {}, STATUS_TIMEOUT);
    if (res.ok) {
      try {
        localStorage.setItem(SEEN_KEY, '1');
      } catch (err) {
        // Storage disabled: only the "not running" wording is lost.
      }
      return res.json();
    }
    // The agent is there but refuses this website: an old agent with the
    // exact-origin list. Not the same as "not installed".
    if (res.status === 403) return { refused: true, origin: window.location.origin };
    return null;
  } catch (err) {
    resolved = null;
    return null;
  }
}

// Why the agent could not be reached, for the modal to say what to do:
// 'blocked' when the browser is set not to let this site reach apps on this
// computer (Chrome's local network access permission), 'not-running' when this
// browser has reached the agent before, otherwise 'not-installed'.
export async function whyUnreachable() {
  if ((await localAccess()) === 'denied') return 'blocked';

  let seen = null;
  try {
    seen = localStorage.getItem(SEEN_KEY);
  } catch (err) {
    seen = null;
  }

  return seen ? 'not-running' : 'not-installed';
}

// Dresden Print runs on Windows 7 and later and macOS 10.12 and later.
export function unsupportedSystem() {
  const ua = navigator.userAgent;
  const mac = ua.match(/Mac OS X 10[_.](\d+)/);
  if (mac && Number(mac[1]) < 12) return true;

  return /Windows NT (5\.|6\.0)/.test(ua);
}

// Hands the agent a short-lived signed URL. The agent fetches the ESC/POS bytes
// itself and writes them straight to the printer.
export async function print(docketUrl) {
  const base = await endpoint();
  if (!base) return { result: PrintResult.NO_AGENT };

  try {
    const res = await withTimeout(
      `${base}/print`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: docketUrl }),
      },
      PRINT_TIMEOUT,
    );

    const body = await res.json().catch(() => ({}));

    if (res.ok) {
      return { result: PrintResult.PRINTED, printer: body.printer };
    }

    return {
      result: PrintResult.FAILED,
      message: body.error || `The printer returned ${res.status}.`,
    };
  } catch (err) {
    // The agent was there a moment ago but has gone; make the next call re-probe.
    resolved = null;
    return { result: PrintResult.NO_AGENT };
  }
}
